import { AttachmentImageUploadModal } from 'components/AttachmentImageUploader';
import Loader from 'components/Loader';
import { showImageError } from 'containers/blog/helper';
import useRequest from 'hooks/useRequest';
import { useRouter } from 'hooks/useRouter';
import { isEmpty, isEqual } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from 'reactstrap';
import { createStructuredSelector } from 'reselect';
import { api } from 'utils/axios';
import { API_BASE_URL, consultationStatusEnum, MODULE_TYPE } from 'utils/constants';
import { getCurrentUserData, getImageUrl, IMAGE_SIZES, showImage } from 'utils/helper';
import { userSelector } from '../../common/globalComponents/selectors';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import ImageUpload from '../helpCenter/ImageUpload';
import UpdateStatusModal from './components/updateStatusModal';
import MessageEditor from './MessageEditor';

const stateSelector = createStructuredSelector({
  user: userSelector,
});
const ConsultationConversation = () => {
  const { user: adminUser } = useSelector(stateSelector);
  const router = useRouter();

  toast.configure();

  const { stgURL } = getCurrentUserData();

  const [inquiryDetails, setInquiryDetails] = useState([]);
  const [inquiryList, setInquiryList] = useState([]);
  const [inquiryIdReplyList, setInquiryIdReplyList] = useState([]);
  const [inquiryPagination, setInquiryPagination] = useState({});
  const [filteredInquiryList, setFilteredInquiryList] = useState({});

  const [activeTabID, setActiveTabID] = useState(router?.query?.id);
  const [activeTabIndex, setActiveTabIndex] = useState();
  const [page, setPage] = useState(1);
  const [currentMessage, setCurrentMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState({});
  const size = 20;
  const endRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();

  const [imageUploadedModal, setImageUploadedModal] = useState(false);
  const IMAGE_TYPE = 'thumbnailMedium';
  const [imageURL, setImageURL] = useState(null);

  const [openUpdateStatusModal, setOpenUpdateStatusModal] = useState(false);

  const toggleUpdateStatusModal = () => {
    setOpenUpdateStatusModal(!openUpdateStatusModal);
  };

  const [activeTab, setActiveTab] = useState(consultationStatusEnum.REQUESTED);
  const tabsList = [
    {
      name: 'Requested',
      filters: {
        status: consultationStatusEnum.REQUESTED,
        deleted: false,
      },
    },
    {
      name: 'Scheduled',
      filters: {
        status: consultationStatusEnum.SCHEDULED,
        deleted: false,
      },
    },
    {
      name: 'OnGoing',
      filters: {
        status: consultationStatusEnum.ONGOING,
        deleted: false,
      },
    },
    {
      name: 'Completed',
      filters: {
        status: consultationStatusEnum.COMPLETED,
        deleted: false,
      },
    },
    {
      name: 'Closed',
      filters: {
        status: consultationStatusEnum.CLOSED,
        deleted: false,
      },
    },
    {
      name: 'Cancelled',
      filters: {
        status: consultationStatusEnum.CANCELLED,
        deleted: false,
      },
    },
    {
      name: 'Deleted',
      filters: {
        deleted: true,
      },
    },
    {
      name: 'All',
      filters: {},
    },
  ];
  const toggleImageUploadedModal = () => {
    setImageUploadedModal(!imageUploadedModal);
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [inquiryIdReplyList]);

  const [updateConsultationStatus, { loading: updateConsultationStatusLoading }] = useRequest({
    onSuccess: () => {
      toast.success('Consultation Status has been updated.');
      window.location.reload();
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const [getConsultationList, { loading: getConsultationLoading }] = useRequest({
    method: 'POST',
    url: '/consultation/admin/search',
    onSuccess: (data) => {
      if (page === 1) {
        setInquiryList(data.results);
      } else {
        setInquiryList((prev) => [...prev, ...data.results]);
      }
      setInquiryPagination(data?.pagination);
      const totalPages = Math.ceil(data?.pagination?.totalCount / data?.pagination?.size);
      setHasMore(page < totalPages);
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const [getConsultationReply, { loading: getConsultationReplyLoading }] = useRequest({
    method: 'GET',
    onSuccess: (response) => {
      setInquiryIdReplyList((prevList) => {
        const sortedResponse = [...response].sort((a, b) => moment(a.createdDate).diff(b.createdDate));

        if (isEmpty(prevList) || !isEqual(prevList, sortedResponse)) {
          return sortedResponse;
        }

        return prevList;
      });
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const [saveConsultationReply, { loading: saveConsultationReplyLoading }] = useRequest({
    method: 'POST',
    onSuccess: (data) => {
      setImageURL(null);
      setSelectedImage(null);
      setCurrentMessage('');
      setInquiryIdReplyList([...inquiryIdReplyList, data]);
      window.location.reload();
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const [getConsultationDetails, { loading: getConsultationDetailsLoading }] = useRequest({
    method: 'GET',
    onSuccess: (data) => {
      setInquiryDetails(data);
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const addReply = () => {
    saveConsultationReply({
      url: `/consultation/admin/${activeTabID}/message`,
      body: {
        content: currentMessage,
        imageId: selectedImage?.id,
      },
    });
  };

  useEffect(() => {
    if (activeTabID) {
      const queryParams = new URLSearchParams();
      queryParams.set('id', activeTabID);
      window.history.replaceState(null, '', `${router.pathname}?${queryParams.toString()}`);

      Promise.all([
        getConsultationReply({ url: `/consultation/admin/${activeTabID}/replies` }),
        getConsultationDetails({ url: `/consultation/admin/${activeTabID}` }),
      ]);
    }
  }, [activeTabID]);

  // Api calling after 10 seconds
  useEffect(() => {
    if (activeTabID) {
      let intervalId;
      const seconds_value = 10000;

      const fetchReplies = () => {
        getConsultationReply({ url: `/consultation/admin/${activeTabID}/replies` });
      };

      fetchReplies();

      intervalId = setInterval(fetchReplies, seconds_value);

      return () => clearInterval(intervalId);
    }
  }, [activeTabID]);

  useEffect(() => {
    if (!isEmpty(inquiryList)) {
      if (!isEmpty(inquiryList) && !router?.query?.id) {
        setActiveTabID(inquiryList[0]?.id);
      }

      setFilteredInquiryList(inquiryList);
    }
  }, [inquiryList]);

  useEffect(() => {
    if (!isEmpty(inquiryList) && !isEmpty(activeTabID)) {
      const active_tab_index = inquiryList?.findIndex((e) => e?.id === activeTabID);
      if (active_tab_index >= 0) {
        setActiveTabIndex(active_tab_index + 1);
      }
    }
  }, [activeTabID, inquiryList]);

  const callListUpdate = useCallback(() => {
    if (!hasMore || getConsultationLoading) return;

    getConsultationList({
      body: {
        page,
        size,
        sortBy: 'updatedDate',
        sortOrder: 1,
        ...tabsList[0]?.filters,
      },
    });
  }, [page, hasMore]);

  useEffect(() => {
    callListUpdate();
  }, [page]);

  const handleGetInquiryDetails = async () => {
    if (activeTabID) {
      const response = await getConsultationDetails({ url: `/consultation/admin/${activeTabID}` });
      if (!isEmpty(response?.status)) {
        const selectedTab = tabsList?.find((e) => e?.name?.toLowerCase() === response?.status);
        setActiveTab(selectedTab?.name?.toLowerCase());
        getConsultationList({
          body: {
            page: 1,
            size,
            sortBy: 'updatedDate',
            sortOrder: 1,
            ...(!isEmpty(selectedTab) ? { ...selectedTab?.filters } : {}),
          },
        });
      }
    }
  };

  useEffect(() => {
    setInquiryList([]);
    setPage(1);

    handleGetInquiryDetails();
  }, []);

  const lastPostElementRef = useCallback(
    (node) => {
      if (!hasMore || getConsultationLoading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setPage((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [hasMore, getConsultationLoading]
  );

  const handleCopyMessage = (message) => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(message)
        .then(() => {
          toast.success('Message copied to clipboard');
        })
        .catch((err) => {
          toast.error('Failed to copy message: ');
        });
    }
  };

  const groupInquiriesByDate = (inquiries) => {
    const groups = {};

    inquiries.forEach((inquiry) => {
      const inquiryDate = moment(inquiry.createdDate);
      let label;

      if (inquiryDate.isSame(moment(), 'day')) {
        label = 'Today';
      } else if (inquiryDate.isSame(moment().subtract(1, 'day'), 'day')) {
        label = 'Yesterday';
      } else {
        label = inquiryDate.format('MMMM DD, YYYY');
      }

      if (!groups[label]) {
        groups[label] = [];
      }
      groups[label].push(inquiry);
    });

    return groups;
  };

  const groupedInquiries = groupInquiriesByDate(inquiryIdReplyList);

  const printDuration = (val) => {
    const now = moment(new Date());
    const end = moment(val);
    var duration = now.diff(end, 'minutes');
    var numdays = Math.floor(duration / 1440);
    var numhours = Math.floor((duration % 1440) / 60);
    var numminutes = Math.floor((duration % 1440) % 60);

    if (numdays > 0) {
      return numdays + ' day';
    } else if (numhours > 0) {
      return numhours + ' hr';
    } else {
      return numminutes + ' min';
    }
  };

  useEffect(() => {
    if (selectedImage) {
      const resURL = selectedImage?.[IMAGE_TYPE] ? selectedImage?.[IMAGE_TYPE] : selectedImage['original'];
      const url = resURL ? showImage(resURL) : '';
      setImageURL(url);
    }
  }, [selectedImage]);

  // const [markCompletedEnquiryRequest, { loading: markCompletedEnquiryLoading }] = useRequest({
  //   method: 'PUT',
  //   onSuccess: () => {
  //     toast.success('Enquiry has been completed');
  //     window.location.reload();
  //   },
  //   onError: (err) => {
  //     toast.error(err?.message?.message || err?.message);
  //   },
  // });

  // const handleCompleteInquiry = async () => {
  //   const result = await confirm({
  //     title: 'Mark Completed',
  //     message: 'Are you sure, you want to mark completed taken on this Inquiry?',
  //     confirmText: 'Confirm',
  //     confirmColor: 'primary',
  //     cancelColor: 'link text-danger',
  //   });

  //   if (result) {
  //     markCompletedEnquiryRequest({
  //       url: `/consultation/admin/${activeTabID}/mark-completed`,
  //     });
  //   }
  // };

  // update consultation status
  const [isShowStatusModal, setIsShowStatusModal] = useState(false);
  const [prescriptionImageModal, setPrescriptionImageModal] = useState(false);
  const [prescription, setPrescription] = useState(null);

  const [isShowMeetingLinkModal, setIsShowMeetingLinkModal] = useState(false);
  const [meetingLinkInputValue, setMeetingLinkInputValue] = useState('');

  // Status modal
  const toggleStatusModal = () => {
    setIsShowStatusModal(!isShowStatusModal);
  };

  // Meeting modal
  const toggleMeetingModal = () => {
    setIsShowMeetingLinkModal(!isShowMeetingLinkModal);

    if (!isEmpty(inquiryDetails?.meetingLink)) {
      setMeetingLinkInputValue(inquiryDetails?.meetingLink);
    }
  };

  const togglePrescriptionImageModal = () => {
    setPrescriptionImageModal(!prescriptionImageModal);
  };

  const [updateEnquiryRequest, { loading: updateEnquiryLoading }] = useRequest({
    method: 'PUT',
    onSuccess: () => {
      toast.success('Enquiry has been updated.');
      window.location.reload();
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const handleSubmit = async () => {
    let updatedPrescription = null;

    if (!isEmpty(prescription)) {
      updatedPrescription = await api({
        method: 'POST',
        url: `${API_BASE_URL}/document`,
        data: {
          title: `Prescription for consultaion with ${inquiryDetails?.doctor?.firstName} ${inquiryDetails?.doctor?.lastName}`,
          refType: 'patient',
          refId: inquiryDetails?.patient?.id,
          type: 'prescriptions',
          attachment: { ...prescription, forType: MODULE_TYPE.PATIENT_TRACKING },
        },
      });
    }

    if (updatedPrescription?.success === false) {
      toast.error('Something went wrong!');
      return;
    }

    await updateEnquiryRequest({
      url: `/consultation/admin/${inquiryDetails?.id}`,
      body: {
        prescriptionId: updatedPrescription?.id || null,
      },
    });
    toggleStatusModal();
  };

  useEffect(() => {
    setPrescription(inquiryDetails?.prescription?.attachment);
  }, [inquiryDetails]);

  const handleSubmitMeetingLink = async () => {
    await updateEnquiryRequest({
      url: `/consultation/admin/${inquiryDetails?.id}`,
      body: {
        meetingLink: meetingLinkInputValue,
      },
    });

    toggleMeetingModal();
  };

  const handleUpdateStatus = async () => {
    await updateConsultationStatus({
      method: 'PUT',
      url: `/consultation/admin/${inquiryDetails?.id}/update-status`,
      body: {
        status: consultationStatusEnum.ONGOING,
      },
    });
  };

  return (
    <div className="page-content">
      <Loader
        isActive={
          getConsultationLoading ||
          getConsultationDetailsLoading ||
          // markCompletedEnquiryLoading ||
          // getConsultationReplyLoading ||
          saveConsultationReplyLoading ||
          updateConsultationStatusLoading ||
          updateEnquiryLoading
        }
      />
      <Container fluid>
        <Breadcrumbs title="Conversations" breadcrumbItem="Conversations List" />

        <Nav tabs>
          {tabsList?.map((elem, index) => {
            return (
              <NavItem key={`nav_${index}`}>
                <NavLink
                  className={activeTab?.toLowerCase() === elem?.name?.toLowerCase() ? 'active' : ''}
                  onClick={() => {
                    setActiveTab(elem?.name?.toLowerCase());
                    getConsultationList({
                      body: {
                        page: 1,
                        size,
                        sortBy: 'updatedDate',
                        sortOrder: 1,
                        ...elem?.filters,
                      },
                    });
                  }}>
                  {elem?.name}
                </NavLink>
              </NavItem>
            );
          })}
        </Nav>

        <Row className="mt-4">
          <Col lg="12">
            <div className="d-lg-flex">
              <div className="chat-leftsidebar me-lg-4">
                <div className="">
                  <div className="chat-leftsidebar-nav">
                    <div>
                      <div className="d-flex align-items-center justify-content-between  mb-3">
                        <h5 className="font-size-14 mb-0">Recent</h5>
                        <p className="mb-0">{inquiryPagination?.totalCount} results found</p>
                      </div>
                      <ul className="list-unstyled chat-list" id="recent-list">
                        <div style={{ height: '550px', overflowY: 'auto' }}>
                          {!isEmpty(filteredInquiryList) &&
                            filteredInquiryList?.map((elem, index) => {
                              return (
                                <li
                                  key={`${elem.id}_${elem.title}`}
                                  className={
                                    elem?.id === activeTabID
                                      ? `active ${
                                          elem?.deleted
                                            ? 'border border-danger'
                                            : inquiryDetails?.status === consultationStatusEnum.COMPLETED
                                            ? 'border border-success'
                                            : ''
                                        } `
                                      : ''
                                  }
                                  ref={filteredInquiryList.length === index + 1 ? lastPostElementRef : null}>
                                  <Link
                                    to="#"
                                    style={
                                      elem?.deleted
                                        ? { backgroundColor: '#ffdede' }
                                        : inquiryDetails?.status === consultationStatusEnum.COMPLETED
                                        ? { backgroundColor: '#d1f1d3' }
                                        : {}
                                    }
                                    onClick={() => {
                                      setActiveTabID(elem?.id);
                                      setActiveTabIndex(index + 1);
                                    }}>
                                    <div className="d-flex">
                                      <div
                                        className="align-self-center me-3 text-white rounded-circle bg-dark d-flex align-items-center justify-content-center"
                                        style={{ width: '20px', height: '20px' }}>
                                        {index + 1}
                                      </div>

                                      <div className="flex-grow-1 overflow-hidden">
                                        <h5 className="text-truncate font-size-14 mb-1">
                                          {elem?.createdBy?.firstName} {elem?.createdBy?.lastName}
                                        </h5>
                                        <p className="text-truncate mb-0" style={{ maxWidth: 250 }}>
                                          {elem?.description}
                                        </p>
                                      </div>
                                      <div className="font-size-11">{printDuration(elem?.updatedDate)}</div>
                                    </div>
                                  </Link>
                                </li>
                              );
                            })}
                        </div>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-100 user-chat">
                <Card>
                  <div className="p-3 border-bottom ">
                    <Row>
                      <Col md={inquiryDetails?.status === consultationStatusEnum.COMPLETED ? '8' : '9'}>
                        <div className="d-flex">
                          <div
                            className=" me-3 text-white rounded-circle bg-dark d-flex align-items-center justify-content-center"
                            style={{ minWidth: '35px', height: '35px', fontSize: '15px' }}>
                            {activeTabIndex}
                          </div>
                          <div>
                            <h5 className="font-size-15 mb-1">
                              {inquiryDetails?.createdBy?.firstName} {inquiryDetails?.createdBy?.lastName}
                            </h5>
                            <p className="text-muted mb-0">{inquiryDetails?.description}</p>
                          </div>
                        </div>
                      </Col>
                      <Col
                        md={inquiryDetails?.status === consultationStatusEnum.COMPLETED ? '4' : '3'}
                        className="d-flex align-items-center justify-content-end">
                        {inquiryDetails?.status === consultationStatusEnum.COMPLETED && (
                          <h6 className="text-success mb-0 me-3">Completed</h6>
                        )}

                        {inquiryDetails?.status === consultationStatusEnum.REQUESTED ? (
                          ''
                        ) : inquiryDetails?.status === consultationStatusEnum.SCHEDULED ? (
                          ''
                        ) : (
                          <Button outline color="dark" size="sm" onClick={() => toggleUpdateStatusModal()}>
                            Update Status
                          </Button>
                        )}
                      </Col>
                    </Row>
                  </div>
                  <div>
                    <div className="chat-conversation p-3">
                      <ul className="list-unstyled mb-0">
                        <div className="pe-4" style={{ maxHeight: '486px', overflowY: 'auto' }}>
                          {!isEmpty(groupedInquiries) ? (
                            Object.entries(groupedInquiries).map(([dateLabel, inquiries], index) => {
                              return (
                                <React.Fragment key={dateLabel}>
                                  <li key={`date-label-${index}`}>
                                    <div className="chat-day-title">
                                      <span className="title">{dateLabel}</span>
                                    </div>
                                  </li>
                                  {inquiries?.map((inquiry, idx) => {
                                    const isPDF = inquiry?.image?.type === 'application/pdf';

                                    return (
                                      <li
                                        key={`inquiry-${inquiry.id}-${idx}`}
                                        className={
                                          inquiry?.createdById !== inquiryDetails?.createdById ? 'right' : 'left'
                                        }>
                                        <div className="conversation-list">
                                          {inquiry?.createdById === adminUser.id && (
                                            <UncontrolledDropdown>
                                              <DropdownToggle href="#" className="dropdown-toggle" tag="a">
                                                <i className="bx bx-dots-vertical-rounded" />
                                              </DropdownToggle>
                                              <DropdownMenu className="dropdown-menu-end">
                                                <DropdownItem
                                                  href="#"
                                                  onClick={() => handleCopyMessage(inquiry?.content)}>
                                                  Copy
                                                </DropdownItem>
                                              </DropdownMenu>
                                            </UncontrolledDropdown>
                                          )}
                                          <div className="ctext-wrap">
                                            <div className="conversation-name">{inquiry?.createdBy?.displayName}</div>
                                            <p
                                              dangerouslySetInnerHTML={{
                                                __html: inquiry?.content?.replaceAll('\n', '<br/>'),
                                              }}
                                            />
                                            {isPDF ? (
                                              <div className="pt-2 pb-3">
                                                <a
                                                  href={getImageUrl(inquiry.image, IMAGE_SIZES.ORIGINAL)}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  className="bg-white p-3 rounded-3">
                                                  <i className="bx bxs-file-pdf me-2 text-danger" />
                                                  <span className="text-dark">{inquiry?.image?.name}</span>
                                                </a>
                                              </div>
                                            ) : inquiry?.image?.thumbnailSmall ? (
                                              <a
                                                href={getImageUrl(inquiry.image, IMAGE_SIZES.ORIGINAL)}
                                                target="_blank"
                                                rel="noreferrer">
                                                <img
                                                  src={getImageUrl(inquiry.image, IMAGE_SIZES.MEDIUM)}
                                                  width={150}
                                                  className="h-100"
                                                />
                                              </a>
                                            ) : null}

                                            <p className={`chat-time mb-0 mt-2`}>
                                              <i className="bx bx-time-five align-middle me-1" />
                                              {moment(inquiry?.createdDate).format('hh:mm')}
                                            </p>
                                          </div>
                                        </div>
                                      </li>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })
                          ) : (
                            <li>
                              <div className="text-center py-5">
                                <i className="bx bxs-error mb-2" style={{ fontSize: '40px', color: '#ffbc42' }} />
                                <h5 className="mb-0">No Messages Found</h5>
                              </div>
                            </li>
                          )}
                          <div ref={endRef} />
                        </div>
                      </ul>
                    </div>
                    <div className="input-wrapper border-top px-3 d-flex justify-content-end bg-light">
                      {imageURL && (
                        <div className="image-preview position-relative me-2 my-4">
                          {imageURL?.endsWith('.pdf') ? (
                            <a href={imageURL} target="_blank" rel="noreferrer" className="bg-white p-2 rounded-3">
                              <i className="bx bxs-file-pdf me-2 text-danger" />
                              <span className="text-dark">View PDF</span>
                            </a>
                          ) : (
                            <img
                              src={imageURL}
                              alt="Selected"
                              className="img-fluid rounded"
                              style={{ maxWidth: '300px', maxHeight: '300px' }}
                            />
                          )}

                          {/* <button
                            type="button"
                            className="btn btn-danger btn-sm position-absolute top-0 start-100 translate-middle"
                            onClick={() => setSelectedImage(null)}
                            style={{ borderRadius: '50%', padding: '2px', fontSize: '10px' }}>
                            <i className="mdi mdi-close"></i>
                          </button> */}
                        </div>
                      )}
                    </div>

                    {inquiryDetails?.status === consultationStatusEnum.REQUESTED ? (
                      ''
                    ) : inquiryDetails?.status === consultationStatusEnum.SCHEDULED ? (
                      <div className="p-3">
                        <button type="button" className="btn btn-primary w-100" onClick={handleUpdateStatus}>
                          Start Conversation
                        </button>
                      </div>
                    ) : (
                      inquiryDetails?.paymentStatus === 'completed' &&
                      !isEmpty(inquiryDetails?.doctor) && (
                        <div className="p-3 chat-input-section">
                          <Row>
                            <Col>
                              <div className="position-relative ">
                                <MessageEditor
                                  addReply={addReply}
                                  currentMessage={currentMessage}
                                  setCurrentMessage={setCurrentMessage}
                                />
                              </div>

                              <div
                                className="chat-input-links"
                                style={{
                                  position: 'absolute',
                                  right: '14px',
                                  top: '18px',
                                }}>
                                <ul className="list-inline mb-0">
                                  <li className="list-inline-item">
                                    <a className="me-2" onClick={toggleImageUploadedModal}>
                                      <i className="mdi mdi-file-image-outline" id="Imagetooltip"></i>
                                      <UncontrolledTooltip placement="top" target="Imagetooltip">
                                        Images
                                      </UncontrolledTooltip>
                                    </a>
                                  </li>
                                </ul>
                              </div>
                            </Col>
                            <Col className="col-auto">
                              <Button
                                type="button"
                                color="primary"
                                aria-label="Send message"
                                onClick={() => addReply()}
                                className="btn-rounded chat-send w-md">
                                <span className="d-none d-sm-inline-block me-2">Send</span>{' '}
                                <i className="mdi mdi-send"></i>
                              </Button>
                            </Col>
                          </Row>
                        </div>
                      )
                    )}
                  </div>
                </Card>

                {inquiryDetails?.status === consultationStatusEnum.REQUESTED ? (
                  ''
                ) : inquiryDetails?.status === consultationStatusEnum.SCHEDULED ? (
                  ''
                ) : (
                  <>
                    {inquiryDetails?.paymentStatus === 'completed' &&
                      !isEmpty(inquiryDetails?.doctor) &&
                      !inquiryDetails?.deleted && (
                        <div className="d-flex align-items-center justify-content-between mt-2 mb-4">
                          <button type="button" className="btn btn-success btn-sm" onClick={toggleStatusModal}>
                            {!isEmpty(inquiryDetails?.prescriptionId) ? 'Update' : 'Upload'} Prescription
                          </button>

                          {!isEmpty(inquiryDetails?.prescription) && (
                            <div>
                              <a
                                className="text-primary"
                                target="_blank"
                                href={getImageUrl(inquiryDetails?.prescription?.attachment, IMAGE_SIZES.ORIGINAL)}>
                                View Prescription
                              </a>
                            </div>
                          )}
                        </div>
                      )}

                    {inquiryDetails?.paymentStatus === 'completed' &&
                      !isEmpty(inquiryDetails?.doctor) &&
                      !inquiryDetails?.deleted && (
                        <button type="button" className="btn btn-info btn-sm mb-4" onClick={toggleMeetingModal}>
                          {!isEmpty(inquiryDetails?.meetingLink) ? 'Update' : 'Add'} Meeting Link
                        </button>
                      )}
                  </>
                )}

                <ImageUpload
                  renameOriginal={true}
                  renameOriginalRaw={true}
                  isOpen={imageUploadedModal}
                  toggle={toggleImageUploadedModal}
                  onUpload={(file) => {
                    toggleImageUploadedModal();
                    setSelectedImage(file);
                  }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Update Status  */}
      <UpdateStatusModal
        isOpen={openUpdateStatusModal}
        toggle={toggleUpdateStatusModal}
        selectedItem={inquiryDetails}
      />

      {/* Upload Prescription  */}
      <Modal isOpen={isShowStatusModal} toggle={toggleStatusModal} centered={true} size="md">
        <ModalHeader toggle={toggleStatusModal}>Update Prescription</ModalHeader>
        <ModalBody className="p-4">
          <FormGroup className="mb-4">
            <Label className="form-label mb-3">Upload Prescription</Label>
            <button type="button" className="btn btn-outline-dark w-100 py-3" onClick={togglePrescriptionImageModal}>
              Add Image
            </button>

            {!isEmpty(prescription) && (
              <div className="mt-4 text-center bg-light rounded-md p-4">
                {!prescription?.originalRaw?.endsWith('.pdf') && (
                  <img
                    style={{
                      maxWidth: 150,
                    }}
                    className="mb-3 p-2"
                    src={
                      !isEmpty(inquiryDetails?.prescription)
                        ? getImageUrl(prescription, IMAGE_SIZES.ORIGINAL)
                        : stgURL + '/upload/' + prescription?.original
                    }
                    onError={(e) => {
                      showImageError(e);
                    }}
                  />
                )}
                <div className="mb-2">
                  <a
                    target="_blank"
                    className="mb-3"
                    href={
                      !isEmpty(inquiryDetails?.prescription)
                        ? getImageUrl(prescription, IMAGE_SIZES.ORIGINAL)
                        : stgURL + '/upload/' + prescription?.original
                    }>
                    {prescription?.name}
                  </a>
                </div>
                <Button
                  color="danger"
                  className="w-100"
                  onClick={() => {
                    setPrescription(null);
                  }}>
                  Remove
                </Button>
              </div>
            )}

            <AttachmentImageUploadModal
              onlyAttachmentUploadCall={true}
              isOpen={prescriptionImageModal}
              toggle={togglePrescriptionImageModal}
              forType={MODULE_TYPE.PATIENT_TRACKING}
              acceptTypes={'image/*,application/pdf'}
              onUpload={(file) => {
                togglePrescriptionImageModal();
                setPrescription(file);
              }}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button className="me-3" color="primary" onClick={handleSubmit}>
            Save
          </Button>
          <Button color="light" onClick={toggleStatusModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Meeting Modal  */}
      <Modal isOpen={isShowMeetingLinkModal} toggle={toggleMeetingModal} centered={true} size="md">
        <ModalHeader toggle={toggleMeetingModal}>
          {inquiryDetails?.meetingLink ? 'Update' : 'Add '} Meeting Link
        </ModalHeader>
        <ModalBody className="p-4">
          <div>
            <Label for={'meetingLink'} className="form-label  ">
              Meeting Link
            </Label>
            <Input
              id={'meetingLink'}
              name={'meetingLink'}
              type="text"
              className="form-control"
              placeholder={'Enter Meeting Link'}
              value={meetingLinkInputValue}
              onChange={(e) => {
                setMeetingLinkInputValue(e.target.value);
              }}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="me-3" color="primary" onClick={handleSubmitMeetingLink}>
            Save
          </Button>
          <Button color="light" onClick={toggleMeetingModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ConsultationConversation;
